/*
 * Copyright (c) 2026 SimplyBook.me Ltd. All rights reserved.
 * Licensed under the Apache License, Version 2.0.
 */

import React from "react";
import styled from "styled-components";
import Flex from "../ui/Flex";
import Stack from "../ui/Stack";
import Text from "../ui/Text";
import TextCircleDivider from "../ui/TextCircleDivider";
import Button from "../ui/Button";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "../../constants/constants";
import Icon from "../ui/Icon";
import FormatDuration from "../formatters/FormatDuration";
import FormatPrice from "../formatters/FormatPrice";
import TreeView from "./tree/TreeView";
import TreeItem from "./tree/TreeItem";
import LineItem from "./LineItem";
import { getServiceBookingUnits, getServiceSessionCount, getServiceTotalPrice, mergeProductAndAddonLines } from "../../utils/service";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/Tooltip";
import TreeChild from "./tree/TreeChild";
import TreeItemChilds from "./tree/TreeItemChilds";
import { useTranslate } from "../../hooks/useTranslate";
import { isClassService } from "../../utils/classes";
import usePluginUtils from "../../hooks/usePluginUtils";
import { useServices } from "../../lib/react-query/useServices";
import Skeleton from "../skeleton/Skeleton";

const ServicesCardMain = styled(Flex)`
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
`;

const ServiceCardMainControl = styled.div`
  margin-left: auto;
`;

const ServicesCardTotals = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;

  ${({ theme }) => theme.containerMedia.sm} {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const ServicesCardIcon = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  border: 1px solid rgba(${({ theme }) => theme.colors.iconStroke.rgba});
`;

const ServiceItemWrapper = styled(Flex)`
  padding-inline: 0.5rem;
  min-width: 0;

  ${({ theme }) => theme.containerMedia.sm} {
    padding-inline: 0;
  }
`;

const ServiceItemInner = styled(Flex)`
  min-width: 0;
  width: 100%;

  ${({ theme }) => theme.containerMedia.sm} {
    align-items: center;
    gap: 0.5rem;
  }
`;

const ServiceItemAdditional = styled(Flex)`
  flex-shrink: 0;
`;

const ChildPrice = styled.div`
  margin-right: ${({ $canDelete }) => ($canDelete ? "49px" : 0)};

  ${({ theme }) => theme.containerMedia.sm} {
    margin-right: 0;
  }
`;

const ServiceItemPrice = styled.div`
  margin-left: auto;

  ${({ theme }) => theme.containerMedia.sm} {
    margin-left: 0;
  }
`;

const StyledTextCircleDivider = styled((props) => <TextCircleDivider {...props} />)`
  ${({ theme }) => theme.containerMedia.sm} {
    display: none;
  }
`;

const MembershipMarker = styled.span`
  color: rgba(${({ theme }) => theme.colors.accent.rgba});
  font-weight: 600;
`;

const AppliedMemberships = styled(Stack)`
  padding: 0.5rem 1rem;
  border-radius: 0.5rem;
  background: rgba(${({ theme }) => theme.colors.accent.rgb}, 0.08);
`;

const StyledTreeItem = styled(TreeItem)`
  ${({ theme }) => theme.containerMedia.sm} {

  &:not(:last-child) {
    padding-bottom: 1rem;
    border-bottom: 1px solid rgba(${({ theme }) => theme.colors.border.rgba});
  }

  &:last-child {
    border-bottom: none;
  }
`;

const ServicesCard = ({ services, canEdit, canDelete, canSelect, onDelete, bookings = [], membershipAccessGrants = {} }) => {
  const { _t } = useTranslate();
  const navigate = useNavigate();
  // Group booking can be only single service
  const [firstService] = services;
  const { isPluginEnabled } = usePluginUtils();
  const needsClassLookup = isPluginEnabled("classes") && firstService.is_class === undefined;
  const { data: classes, isLoading: isClassLookupLoading } = useServices({ "filter[type]": "class" }, { enabled: needsClassLookup });
  const isClass = isClassService(firstService) || !!classes?.some((item) => item.id === firstService.id);

  // Distinct memberships in service order, each with a marker (*, **, …) and a
  // usage tally (credits drawn). Service rows and the summary both read byMembershipId.
  const appliedMemberships = [];
  const byMembershipId = {};
  services.forEach((service) => {
    const id = membershipAccessGrants[service.id]?.clientMembershipId;
    if (id == null) return;
    if (!byMembershipId[id]) {
      byMembershipId[id] = {
        id,
        grant: membershipAccessGrants[service.id],
        marker: "*".repeat(appliedMemberships.length + 1),
        usage: 0,
      };
      appliedMemberships.push(byMembershipId[id]);
    }
    byMembershipId[id].usage += getServiceBookingUnits(service);
  });
  const isGroupBooking = Boolean(firstService.group_booking_count);
  const groupBookingCount = isGroupBooking ? firstService.group_booking_count : 0;


  const onlineMeetingLinks = bookings.reduce((acc, booking) => {
    if (booking.online_meeting_link && booking.service?.id) {
      acc[booking.service.id] = booking.online_meeting_link;
    }
    return acc;
  }, {});

  const servicesTotalPrice = services.reduce((acc, service) => acc + getServiceTotalPrice(service, true), 0);
  const servicesTotalDuration = services.reduce((acc, service) => acc + service.duration, 0);

  return (
    <Stack $gap="1.5rem">
      <ServicesCardMain $align="center" $gap="1.5rem">
        <ServicesCardIcon>
          {isClassLookupLoading
            ? <Skeleton width={20} height={20} borderRadius="50%" />
            : isClass ? <Icon.SelectClass size={Icon.SIZE.SM} /> : <Icon.SelectServices size={Icon.SIZE.SM} />}
        </ServicesCardIcon>
        <Stack $gap="0.25rem">
          {isClassLookupLoading
            ? <Skeleton width={64} height="1em" borderRadius={4} />
            : <Text>{isClass ? "Classes" : "Services"}</Text>}
          <ServicesCardTotals>
            {!!servicesTotalPrice && (
              <Text size="large" color="dark" weight="bold">
                <FormatPrice value={servicesTotalPrice} currency={services[0].currency} />
              </Text>
            )}
            <Flex $align="center">
              {!!servicesTotalPrice && !!servicesTotalDuration && <StyledTextCircleDivider/>}
              {!!servicesTotalDuration && (
                <Text>
                  <FormatDuration value={servicesTotalDuration} type={FormatDuration.TYPE.HOUR} />
                </Text>
              )}
            </Flex>
          </ServicesCardTotals>
        </Stack>
        <ServiceCardMainControl>
          {(canEdit || canSelect) && !isClassLookupLoading && (
            <Button
              variant="transparent"
              size="icon"
              icon={
                (canEdit && <Icon.PencilAndPaper color="secondary" />) ||
                (canSelect && <Icon.ChevronRight color="secondary" />)
              }
              onClick={() => navigate(isClass ? ROUTES.CLASSES : ROUTES.SERVICES)}
              aria-label={_t(isClass
                ? (canEdit ? "Edit class" : "Select class")
                : (canEdit ? "Edit services" : "Select services"))}
            />
          )}
        </ServiceCardMainControl>
      </ServicesCardMain>
      <Stack $gap="1rem">
        {services.length > 0 && (
          <TreeView>
            {services.map((service) => {
              const grant = membershipAccessGrants[service.id];
              const marker = grant ? byMembershipId[grant.clientMembershipId]?.marker || "" : "";

              return (
                <StyledTreeItem key={service.id}>
                  <ServiceItemWrapper $gap="0.5rem">
                    <ServiceItemInner $align="center" $justify="space-between" $gap="1rem">
                      <LineItem
                        applyMobileStyles
                        quantity={getServiceSessionCount(service)}
                        name={marker ? <>{service.name}<MembershipMarker>{marker}</MembershipMarker></> : service.name}
                        metadata={[
                          service.duration > 0 && (
                            <Text key="duration">
                              <FormatDuration value={service.duration} type={FormatDuration.TYPE.HOUR} />
                            </Text>
                          ),
                          isGroupBooking && (
                            <Flex $align="center" $gap="0.125rem" key="group">
                              <Text>{groupBookingCount}</Text>
                              <Icon.Users color="secondary" />
                            </Flex>
                          ),
                        ]}
                      />
                      {getServiceTotalPrice(service, true) != null && (
                        <ServiceItemPrice>
                          <Text color="dark" weight="bold">
                            <FormatPrice value={getServiceTotalPrice(service, true)} currency={service.currency} />
                          </Text>
                        </ServiceItemPrice>
                      )}
                    </ServiceItemInner>
                    {(canDelete || onlineMeetingLinks[service.id]) && (
                      <ServiceItemAdditional $align="center" $gap="1rem">
                        {onlineMeetingLinks[service.id] && (
                          <Tooltip>
                            <TooltipTrigger>
                              <Button
                                variant="outline"
                                size="icon"
                                icon={<Icon.VideoIcon color="secondary" />}
                                href={onlineMeetingLinks[service.id]}
                                target="_blank"
                                aria-label={_t("Open online meeting")}
                              />
                            </TooltipTrigger>
                            <TooltipContent><Text>Open online meeting</Text></TooltipContent>
                          </Tooltip>
                        )}
                        {canDelete && (
                          <Button
                            variant="transparent"
                            size="icon"
                            icon={<Icon.Trash color="iconStroke" />}
                            onClick={(e) => onDelete(e, service.id)}
                            aria-label={_t("Remove service")}
                          />
                        )}
                      </ServiceItemAdditional>
                    )}
                  </ServiceItemWrapper>
                  {(service.addons?.length > 0 || service.products?.length > 0) &&
                    <TreeItemChilds>
                      {(() => {
                        const serviceProducts = mergeProductAndAddonLines(service.addons, service.products, groupBookingCount);
                        const lastProduct = serviceProducts[serviceProducts.length - 1];
                        return serviceProducts.map((product) => {
                          const productDuration = product.product ? product.product.duration : product.duration;
                          return (
                          <TreeChild key={product.id} $isLast={product.id === lastProduct.id}>
                          <LineItem
                            applyMobileStyles
                            quantity={product.qty}
                            name={product.product ? product.product.name : product.name}
                            metadata={[
                              productDuration > 0 && (
                                <Text key="duration">
                                  <FormatDuration value={productDuration} type={FormatDuration.TYPE.HOUR} />
                                </Text>
                              ),
                            ]}
                          />
                          {product.totalAmount > 0 && <ChildPrice $canDelete={canDelete}>
                            <Text color="dark" weight="bold">
                              <FormatPrice value={product.totalAmount} currency={product.currency} />
                            </Text>
                          </ChildPrice>}
                        </TreeChild>
                          );
                        });
                      })()}
                    </TreeItemChilds>
                  }
                </StyledTreeItem>
              );
            })}
          </TreeView>
        )}

        {appliedMemberships.length > 0 && (
          <AppliedMemberships $gap="0.25rem">
            <Text size="small" color="dark">Booking with</Text>
            {appliedMemberships.map(({ id, grant, marker, usage }) => {
              const hasCounts = !grant.isUnlimited && Number.isFinite(grant.rest) && Number.isFinite(grant.used);

              return (
                <Flex key={id} $gap="0.25rem" $align="center" $wrap="wrap">
                  <MembershipMarker>{marker}</MembershipMarker>
                  <Text color="dark" size="small" weight="bold">{grant.name}</Text>
                  {(hasCounts || grant.isUnlimited) && <StyledTextCircleDivider color="dark" size={4} />}
                  {grant.isUnlimited && <Text color="dark" size="small">{_t("Unlimited")}</Text>}
                  {hasCounts && (
                    <Text color="dark" size="small">
                      {usage === 1
                        ? _t("1 session will be deducted — %left% of %total% left", {
                          left: grant.rest - usage,
                          total: grant.rest + grant.used,
                        })
                        : _t("%deducted% sessions will be deducted — %left% of %total% left", {
                          deducted: usage,
                          left: grant.rest - usage,
                          total: grant.rest + grant.used,
                        })}
                    </Text>
                  )}
                </Flex>
              );
            })}
          </AppliedMemberships>
        )}
      </Stack>
    </Stack>
  );
};

export default ServicesCard;
